import { Component, OnDestroy, OnInit } from '@angular/core';
import { FieldArrayType } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import { AXErrorMessageDisplay, AXErrorMessages } from './../../../../models/formly.model';
import { AxService } from './../../../../services/application/ax.service';
import { AuthenticationService } from './../../../../services/utilities/authentication.service';
import {
    ButtonType,
    Colors,
    Size,
    Icons,
    StorageObjects,
    UserType,
    Schemas
} from '../../../../utilities/enum';
import { AlertService } from './../../../../services/utilities/alert.service';
import { VariableService } from './../../../../services/utilities/variables.service';
import { StorageService } from '@yellowtail-frontend/storage-service';
import { CdmService } from '../../../../services/application/cdm.service';
import { HelperService } from '../../../../services/utilities/helper.service';
import { TenantService } from '../../../../services/application/tenant.service';
import { FormGroup } from '@angular/forms';

@Component({
    selector: 'app-array',
    templateUrl: './array.component.html',
    styleUrls: ['./array.component.scss'],
    standalone: false
})
export class ArrayComponent extends FieldArrayType implements OnInit, OnDestroy {
    expandable: boolean;
    open: boolean;
    icons = Icons;
    colors = Colors;
    size = Size;
    buttonType = ButtonType;
    schemas = Schemas;
    currentArrayLength: number = 0;
    totalAllowedArrayLength: number = 0;

    displayText = '';
    cord_ref = '';
    allowAddingItemsToArray = true;

    axErrorObject: AXErrorMessages = { count: 0, errors: [], type: null };
    axErrorDisplayObject: AXErrorMessageDisplay = {};

    private subscription = new Subscription();
    private subscriptionHiddenFields = new Subscription();

    constructor(
        private readonly _axService: AxService,
        private readonly _cdmService: CdmService,
        private readonly _variableService: VariableService,
        private readonly _alertService: AlertService,
        private readonly _storageService: StorageService,
        private readonly _helperService: HelperService,
        private readonly _authenticationService: AuthenticationService,
        private readonly _tenantService: TenantService
    ) {
        super();
        this.axErrorDisplayObject.errors = [];
    }

    ngOnInit(): void {
        this.currentArrayLength = this.field?.fieldGroup?.length;
        this.totalAllowedArrayLength = parseInt(this.field?.templateOptions?.maxItems, 10);

        if (this.to.description && this.to.description !== 'oneOf') {
            this.expandable = JSON.parse(this.to.description)?.expandable;
        }

        if (this.to.label !== '') {
            const _titleReplacement = this._variableService.axDisplayTextLookUpData.find(x => x.key === this.to.label);
            if (_titleReplacement) {
                this.displayText = _titleReplacement.value;
            } else {
                this.displayText = this.to.label;
            }
        } else {
            const _titleReplacement = this._variableService.axDisplayTextLookUpData.find(x => x.key === this.field.key.toString());
            if (_titleReplacement) {
                this.displayText = _titleReplacement.value;
            } else {
                this.displayText = this.field.key.toString();
            }
        }

        if (this.field.fieldGroup.length > 0) {
            if (this.field.key === 'Hypotheekgever' && !this.field.model.Cord_Ref) {
                this.field.model.splice(0, 1);
                const GUID  = this._helperService.newIdRef();
                this.model.splice(0, 0, Object.assign({RefPartijNAWData: {IDREF: GUID}}));
                this.field.parent.model.PartijNAWData.splice(1, 0, Object.assign({ID: GUID, special: "Hypotheekgever"}));
                this.field['showAddButton'] = true;
            } else {
                let index = 0;
                for (const obj in this.field?.fieldGroup) {
                    if (obj) {
                        if (this.field.fieldGroup.hasOwnProperty(obj)) {
                            this.field.fieldGroup[obj]['above'] = this.displayText;
                            this.field.fieldGroup[obj]['allowAddingItemsToArray'] = this.allowAddingItemsToArray;
                            this.allowAddingItemsToArray = true;
                            this.field['showAddButton'] = true;
    
                            if (this.field.fieldGroup && this.field.fieldGroup.length >= this.field.templateOptions.maxItems) {
                                this.field.fieldGroup[obj]['allowAddingItemsToArray'] = false;
                                this.allowAddingItemsToArray = false;
                                this.field['showAddButton'] = false;
                            }
                        }
                        index++;
                    }
    
                    if (this._variableService.formlyParent === 'overview' && this.field.key === 'PartijNAWData') {
                        // We no longer allow the user to manually add a PERSON object, they must
                        // add a corresponding object to add the PERSON, for example a NOTARIS object.
                        this.field['showAddButton'] = false;
                    } else {
                        this.field['showAddButton'] = true;
                    }
                }
            }
        } else {
            this.field['showAddButton'] = true;
        }

        // Hide all buttons for advisors on the gegevens page
        // Agents and SuperAgents are not able to add or delete items from a Dossier application.
        const _userType = this._storageService.get<string>(StorageObjects.userType);
        if ((_userType === UserType[UserType.Agent] || _userType === UserType[UserType.SuperAgent])
            && this._variableService.formlyParent === 'details'
        ) {
            this.field['showAddButton'] = false;
        }


        // ERROR CHECKS
        this.subscription.add(this._axService.alerts$.subscribe((errors) => {
            this.axErrorDisplayObject.errors = [];
            this.axErrorObject = { count: 0, errors: [], type: null };
            if (errors && errors.length > 0) {

                let MATCHING_REF_ERROR;
                for (const error of errors) {
                    if (this.model && error?.Id?.toLowerCase() === this.model['Cord_Ref']?.toLowerCase()) {
                        MATCHING_REF_ERROR = error;
                        break;
                    } else if (this.model && error?.Property?.toLowerCase() === this.to.label.toLowerCase()) {
                        /// THE ERROR/ WARNING IS ON THE PARENT ITSELF
                        MATCHING_REF_ERROR = error;
                        break;
                    }
                }

                if (MATCHING_REF_ERROR) {
                    this.axErrorObject.count = 1;
                    this.axErrorObject.type = MATCHING_REF_ERROR.Type.toLowerCase();
                }

                // Matching error
                if (this.axErrorObject.count > 0) {

                    if (this.model.hasOwnProperty(MATCHING_REF_ERROR.Property)) {
                        const value = this.model[MATCHING_REF_ERROR.Property];
                        if (value instanceof Object) {
                            this.axErrorDisplayObject.type = MATCHING_REF_ERROR.Type.toLowerCase() || 'error';
                            if (MATCHING_REF_ERROR.Message.toLowerCase() !== 'manualentry') {
                                this.axErrorDisplayObject.errors.push(MATCHING_REF_ERROR.Message);
                            }
                        }
                    } else {
                        if (MATCHING_REF_ERROR.Message.toLowerCase() !== 'manualentry') {
                            this.axErrorDisplayObject.type = MATCHING_REF_ERROR.Type.toLowerCase() || 'error';
                            this.axErrorDisplayObject.errors.push(MATCHING_REF_ERROR.Message);
                        }
                    }
                }

                // Add UI messages
                this._helperService.addFormlyUIErrorMessages(this.axErrorObject, this.to?.label?.toString(), this.model?.Cord_Ref);
            }
        }));

        this.subscriptionHiddenFields.add(this._cdmService.hiddenFields.subscribe((fields) => {
                this.field.hide = this._cdmService.hideFields(fields, this.field);
            })
        );


    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        this.subscriptionHiddenFields?.unsubscribe();
    }

    add(event?: any): void {

        event?.stopPropagation();
        const i = this.field.fieldGroup.length;

        
        let tempID = '';
        if (this._tenantService.hasAccessToSchema(this.schemas.BuyToLetAanvraag)) {
            switch (this.field.key) {
                case 'LegalPersons':
                    tempID = this._helperService.newGuid(this._helperService.legalPersonIdFormat);
                    break;
                case 'Person':
                    tempID = this._helperService.newGuid(this._helperService.personIdFormat);
                    break;
                case 'RealEstates':
                    tempID = this._helperService.newGuid(this._helperService.realEstateIdFormat);
                    break;
                case 'Mortgages':
                    tempID = this._helperService.newGuid(this._helperService.mortgageIdFormat);
                    break;
                case 'LoanPart':
                    tempID = this._helperService.newGuid(this._helperService.loanPartIdFormat);
                    break;
            }
            this.model.splice(i, 0, Object.assign({ ID: tempID, isDeleted: false }));
            this.field.fieldGroup[i]['ID'] = tempID;
        } else {
            if (!this.model) {
                this.options.build();
            }
            
            const GUID  = this._helperService.newIdRef();
            const PERSON_LENGTH: number = this.field.parent.model.PartijNAWData?.length;
            if (this.field.key.toString().toLowerCase() === 'hypotheekgever') {
                this.model.splice(i, 0, Object.assign({RefPartijNAWData: {IDREF: GUID}}));
                this.field.parent.model.PartijNAWData.splice(PERSON_LENGTH, 0, Object.assign({ID: GUID, special: "Hypotheekgever"}));
            } else if (this.field.key.toString().toLowerCase() === 'tussenpersoon') {
                this.model.splice(i, 0, Object.assign({RefContactPersoonData: {IDREF: GUID}}));
                this.field.parent.model.PartijNAWData.splice(PERSON_LENGTH, 0, Object.assign({ID: GUID, special: "Tussenpersoon"}));
            } else if (this.field.key.toString().toLowerCase() === 'notaris') {
                this.model.splice(i, 0, Object.assign({RefContactPersoonData: {IDREF: GUID}}, ));
                this.field.parent.model.PartijNAWData.splice(PERSON_LENGTH, 0, Object.assign({ID: GUID, special: "Notaris"}));
            } else {
                this.model.splice(i, 0, Object.assign({}));
            }
        }

        

        this.options.build();
        this.formControl.markAsDirty();

        this.totalAllowedArrayLength = parseInt(this.field?.templateOptions?.maxItems, 10);

        if (this.field.fieldGroup.length >= this.totalAllowedArrayLength) {
            this.field['showAddButton'] = false;
        }

        const FORM_GROUP_TO_CHECK: FormGroup = this.formControl.parent as FormGroup;

        Object.keys(FORM_GROUP_TO_CHECK.controls).forEach((key) => {
            const FIELD_GROUP = this.field.parent?.fieldGroup.find((field) => field.key === key);
            const result = this.checkIfAllValuesNull(FORM_GROUP_TO_CHECK.controls[key], key, FIELD_GROUP);
            if (result) {
                const FORM_GROUP = FORM_GROUP_TO_CHECK.controls[key] as any;
                if (FORM_GROUP.controls) {
                    const FIELD_GROUP = this.field.parent?.fieldGroup.find((field) => field.key === key);
                    if (FIELD_GROUP) {
                        const FIELD_TYPE_ONE_OF = FIELD_GROUP.templateOptions?.description?.toLowerCase() === 'oneof';
                        if (FIELD_TYPE_ONE_OF) {
                            this.removeValidatorsFromNullObjects(FORM_GROUP_TO_CHECK.controls[key] as FormGroup);
                        }
                    }
                }
            }
        });
    }

    checkIfAllValuesNull(data: any, key: string, fieldGroup: any): any {
        let RETURN_VALUE = true;
        if (data.controls) {
            Object.keys(data.controls).forEach(key => {
                const CONTROL = data.controls[key];
    
                if (!CONTROL.controls) {
                    const CONTROL_VALUE = CONTROL.value;
                    if (CONTROL_VALUE === null || CONTROL_VALUE === undefined || CONTROL.status === 'DISABLED') {
                        const result = true;
                    } else {
                        RETURN_VALUE = false;
                    }
                    // if NULL then this control is empty
                } else {
                    const FIELD_GROUP_CHILD = fieldGroup.fieldGroup.find((field) => field.key === key);
                    this.checkIfAllValuesNull(CONTROL, key, FIELD_GROUP_CHILD);
                }
            });
        } else {
            const CONTROL_VALUE = data.value;
            if (CONTROL_VALUE === null || CONTROL_VALUE === undefined || data.status === 'DISABLED') {
                const result = true;
            } else {
                RETURN_VALUE = false;
            }
        }

        if (fieldGroup) {
            const FIELD_TYPE_ONE_OF = fieldGroup.templateOptions?.description?.toLowerCase() === 'oneof';
            if (FIELD_TYPE_ONE_OF) {
                this.removeValidatorsFromNullObjects(data);
            }
        }

        return RETURN_VALUE;
    }

    removeValidatorsFromNullObjects(data: FormGroup): void {
        if (data.controls) {
            Object.keys(data.controls).forEach(key => {
                const CONTROL_FORMGROUP = data.controls[key] as FormGroup;
                if (CONTROL_FORMGROUP.controls) {
                    this.removeValidatorsFromNullObjects(CONTROL_FORMGROUP);
                }
    
                data.controls[key].clearValidators();
                data.controls[key].updateValueAndValidity();
              });
        }
    }
}
